// Local-time helpers. Phase 1 supports only fixed-offset zones (no DST).
const MINUTE = 60_000;
const DAY = 86_400_000;

export const OFFSETS: Record<string, number> = { 'Asia/Bangkok': 420 };

export function offsetFor(tz: string): number {
  const off = OFFSETS[tz];
  if (off === undefined) throw new Error(`Unsupported timezone: ${tz}`);
  return off;
}

function shifted(at: Date | number, off: number): Date {
  const ms = typeof at === 'number' ? at : at.getTime();
  return new Date(ms + off * MINUTE);
}

export function localDate(at: Date | number, off: number): string {
  return shifted(at, off).toISOString().slice(0, 10);
}

export function localMinutes(at: Date | number, off: number): number {
  const d = shifted(at, off);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

function parseDate(date: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error(`Invalid date: ${date}`);
  const ms = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(ms) || new Date(ms).toISOString().slice(0, 10) !== date) throw new Error(`Invalid date: ${date}`);
  return ms;
}

export function isValidDate(date: string): boolean {
  try {
    parseDate(date);
    return true;
  } catch {
    return false;
  }
}

export function instantAt(date: string, minutes: number, off: number): Date {
  return new Date(parseDate(date) + (minutes - off) * MINUTE);
}

export function addDays(date: string, n: number): string {
  return new Date(parseDate(date) + n * DAY).toISOString().slice(0, 10);
}

export function weekday(date: string): number {
  return new Date(parseDate(date)).getUTCDay();
}

export function parseHHMM(s: string): number {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(s);
  if (!m) throw new Error(`Invalid time: ${s}`);
  return Number(m[1]) * 60 + Number(m[2]);
}

export function fmtHHMM(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const TH_WEEKDAYS = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'];
const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

export function fmtThaiDate(date: string): string {
  const d = new Date(parseDate(date));
  return `${TH_WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${TH_MONTHS[d.getUTCMonth()]}`;
}

const ISO_INSTANT = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;

/** Epoch ms for an ISO-8601 instant with an explicit Z or ±hh:mm offset; null otherwise. */
export function parseIsoInstant(raw: unknown): number | null {
  if (typeof raw !== 'string') return null;
  const m = ISO_INSTANT.exec(raw);
  if (!m || !isValidDate(m[1])) return null;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : ms;
}
